import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API } from "../App";
import axios from "axios";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Users, Search, Filter, X, Target, Calendar, Award, Instagram, ArrowLeftRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

// West Ham styled player silhouette fallback
const PlayerFallback = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-gradient-to-br from-[#7C2C3B] to-[#63232F]`}>
    <svg viewBox="0 0 100 100" className="w-24 h-24 opacity-70">
      {/* Player silhouette */}
      <circle cx="50" cy="25" r="15" fill="#F5D76E"/>
      <ellipse cx="50" cy="70" rx="25" ry="30" fill="#F5D76E"/>
      {/* West Ham crossed hammers */}
      <g transform="translate(30, 45) scale(0.4)">
        <rect x="10" y="30" width="80" height="12" rx="2" fill="#2BAEE5" transform="rotate(-45 50 36)"/>
        <rect x="10" y="30" width="80" height="12" rx="2" fill="#2BAEE5" transform="rotate(45 50 36)"/>
      </g>
    </svg>
  </div>
);

const PlayerImage = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (hasError || !src) {
    return <PlayerFallback className={className} />;
  }

  return (
    <img 
      src={src} 
      alt={alt}
      className={className}
      onError={() => setHasError(true)}
      loading="lazy"
    />
  );
};

const PlayerCard = ({ player }) => (
  <Link
    to={player.slug ? `/players/${player.slug}` : "#"}
    className="player-card cursor-pointer group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all block"
    data-testid={`player-${player.id}`}
  >
    <div className="relative h-48 bg-slate-100 overflow-hidden">
      <PlayerImage 
        src={player.image_url} 
        alt={player.name}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
        <span className="bg-[#7C2C3B] text-white text-xs px-2 py-1 rounded font-medium">{player.positions?.[0] || 'Player'}</span>
        {player.team && (
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            player.team === "Women's First Team" 
              ? 'bg-[#2BAEE5] text-white' 
              : 'bg-[#0F172A]/80 text-white'
          }`}>
            {player.team === "Women's First Team" ? "Women" : "Men"}
          </span>
        )}
      </div>
      {/* Social media icons */}
      {(player.instagram || player.tiktok) && (
        <div className="absolute bottom-2 left-2 flex gap-1">
          {player.instagram && (
            <span className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
              <Instagram className="w-3.5 h-3.5 text-pink-600" />
            </span>
          )}
          {player.tiktok && (
            <span className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center">
              <TikTokIcon className="w-3.5 h-3.5 text-black" />
            </span>
          )}
        </div>
      )}
    </div>
    <div className="p-4">
      <h3 className="text-lg font-bold text-[#0F172A] mb-1 truncate" style={{ fontFamily: 'Barlow Condensed' }}>
        {player.name.toUpperCase()}
      </h3>
      <p className="text-sm text-slate-500 mb-3">{player.years_played || player.years_active || 'Historical'}</p>
      
      {/* Stats Row - Games & Goals */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-[#2BAEE5]/10 rounded-lg p-2 text-center">
          <div className="text-xl font-bold text-[#2BAEE5]" style={{ fontFamily: 'Barlow Condensed' }}>
            {player.appearances || 0}
          </div>
          <div className="text-xs text-slate-600 font-medium">GAMES</div>
        </div>
        <div className="bg-[#7C2C3B]/10 rounded-lg p-2 text-center">
          <div className="text-xl font-bold text-[#7C2C3B]" style={{ fontFamily: 'Barlow Condensed' }}>
            {player.goals || 0}
          </div>
          <div className="text-xs text-slate-600 font-medium">GOALS</div>
        </div>
      </div>
    </div>
  </Link>
);

const PlayerModal = ({ player, onClose }) => (
  <Dialog open={!!player} onOpenChange={() => onClose()}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      {player && (
        <>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
              {player.name.toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <PlayerImage 
                src={player.image_url} 
                alt={player.name}
                className="w-full h-64 object-cover rounded-lg"
              />
              
              {/* Social Media Links */}
              {(player.instagram || player.tiktok) && (
                <div className="mt-4 flex gap-3">
                  {player.instagram && (
                    <a 
                      href={`https://instagram.com/${player.instagram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="instagram-link"
                    >
                      <Instagram className="w-5 h-5" />
                      <span className="text-sm font-medium">@{player.instagram}</span>
                    </a>
                  )}
                  {player.tiktok && (
                    <a 
                      href={`https://tiktok.com/@${player.tiktok}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                      data-testid="tiktok-link"
                    >
                      <TikTokIcon className="w-5 h-5" />
                      <span className="text-sm font-medium">@{player.tiktok}</span>
                    </a>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              {/* Stats Cards - Prominent Display */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-xl p-4 text-white text-center">
                  <div className="text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>{player.appearances || 0}</div>
                  <div className="text-sm text-white/80 font-medium uppercase tracking-wide">Games Played</div>
                </div>
                <div className="bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] rounded-xl p-4 text-white text-center">
                  <div className="text-4xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>{player.goals || 0}</div>
                  <div className="text-sm text-white/80 font-medium uppercase tracking-wide">Goals Scored</div>
                </div>
              </div>

              {/* Goals per Game Ratio */}
              {player.appearances > 0 && (
                <div className="bg-slate-100 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
                    {(player.goals / player.appearances).toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500 uppercase">Goals per Game</div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Years at Club</h4>
                <p className="text-lg font-bold text-[#0F172A]">{player.years_active || player.years_played || 'Historical'}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Positions</h4>
                <div className="flex flex-wrap gap-2 mt-1">
                  {player.positions?.length > 0 ? player.positions.map((pos, idx) => (
                    <span key={idx} className="bg-[#7C2C3B] text-white text-xs px-3 py-1 rounded-full font-medium">{pos}</span>
                  )) : (
                    <span className="text-slate-500 text-sm">Not specified</span>
                  )}
                </div>
              </div>
              
              {player.is_current_squad && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <span className="text-green-700 text-sm font-medium">✓ Current 2025-26 Squad</span>
                </div>
              )}
            </div>
          </div>
          
          {player.biography && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">Career Summary</h4>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{player.biography}</p>
            </div>
          )}
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function PlayersPage() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("all");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState("all");
  const [selectedEra, setSelectedEra] = useState("all");

  const positions = ["Goalkeeper", "Defender", "Midfielder", "Forward"];
  const teams = ["Men's First Team", "Women's First Team"];
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  
  // Era filters with decade ranges
  const eras = [
    { id: "all", label: "All Eras", range: null },
    { id: "founders", label: "Founders (1895-1919)", range: [1895, 1919], icon: "🏭" },
    { id: "interwar", label: "Inter-War (1920-1945)", range: [1920, 1945], icon: "⚒️" },
    { id: "postwar", label: "Post-War (1946-1965)", range: [1946, 1965], icon: "🏟️" },
    { id: "worldcup", label: "World Cup Era (1966-1979)", range: [1966, 1979], icon: "🏆" },
    { id: "modern", label: "Modern Era (1980-1999)", range: [1980, 1999], icon: "⚽" },
    { id: "millennium", label: "Millennium (2000-2015)", range: [2000, 2015], icon: "🌟" },
    { id: "current", label: "Current Era (2016+)", range: [2016, 2030], icon: "🔥" },
  ];

  useEffect(() => {
    fetchPlayers();
  }, [selectedPosition, searchQuery, selectedTeam]);

  const fetchPlayers = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedPosition !== "all") params.append("position", selectedPosition);
      if (searchQuery) params.append("search", searchQuery);
      if (selectedTeam !== "all") params.append("team", selectedTeam);
      params.append("all_results", "true"); // Get all results for client-side era filtering
      
      const response = await axios.get(`${API}/players?${params.toString()}`);
      // Handle both old array format and new paginated format
      const playerData = response.data.players || response.data;
      setPlayers(Array.isArray(playerData) ? playerData : []);
    } catch (error) {
      console.error("Failed to fetch players:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get player's start year from years_active
  const getPlayerStartYear = (player) => {
    const years = player.years_active || "";
    if (years && years.includes("-")) {
      const startYear = parseInt(years.split("-")[0]);
      if (!isNaN(startYear)) return startYear;
    }
    return null;
  };

  // Filter by era first
  const eraFilteredPlayers = selectedEra === "all" 
    ? players 
    : players.filter(p => {
        const startYear = getPlayerStartYear(p);
        if (!startYear) return false;
        const era = eras.find(e => e.id === selectedEra);
        if (!era || !era.range) return true;
        return startYear >= era.range[0] && startYear <= era.range[1];
      });

  // Filter players by selected letter and get all from that letter onwards
  const filteredPlayers = selectedLetter === "all" 
    ? eraFilteredPlayers 
    : eraFilteredPlayers.filter(p => {
        const firstLetter = p.name?.charAt(0).toUpperCase();
        return firstLetter >= selectedLetter;
      });

  // Count players per era for badges
  const eraCountsMap = eras.reduce((acc, era) => {
    if (era.id === "all") {
      acc[era.id] = players.length;
    } else {
      acc[era.id] = players.filter(p => {
        const startYear = getPlayerStartYear(p);
        if (!startYear || !era.range) return false;
        return startYear >= era.range[0] && startYear <= era.range[1];
      }).length;
    }
    return acc;
  }, {});

  // Get available letters (letters that have players in current era filter)
  const availableLetters = new Set(
    eraFilteredPlayers.map(p => p.name?.charAt(0).toUpperCase()).filter(Boolean)
  );

  // Group players by first letter for display
  const groupedPlayers = filteredPlayers.reduce((groups, player) => {
    const letter = player.name?.charAt(0).toUpperCase() || '#';
    if (!groups[letter]) groups[letter] = [];
    groups[letter].push(player);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="players-page">
      <SEO title="Players" path="/players" description="West Ham United squad profiles. Browse current and historic players from the men's first team, women's team, U21s and U18s with stats, career summaries and images." />
      <Navbar />
      
      {/* Header */}
      <div className="bg-[#7C2C3B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              PLAYER PROFILES
            </h1>
            <p className="text-white/80">Every player to wear the claret and blue since 1895</p>
          </div>
          <Link
            to="/players/compare"
            className="inline-flex items-center gap-2 bg-[#F8D742] text-[#0F172A] px-4 py-2 rounded-lg text-sm font-bold hover:bg-yellow-300 transition-colors self-start md:self-auto"
            data-testid="player-compare-link"
          >
            <ArrowLeftRight className="w-4 h-4" />
            Compare Two Players
          </Link>
        </div>
      </div>

      {/* Era Filter Tabs */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1e293b] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto scrollbar-hide py-3 gap-2" data-testid="era-filters">
            {eras.map(era => (
              <button
                key={era.id}
                onClick={() => {
                  setSelectedEra(era.id);
                  setSelectedLetter("all"); // Reset letter filter when changing era
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  selectedEra === era.id
                    ? "bg-[#7C2C3B] text-white shadow-lg"
                    : "bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-white"
                }`}
                data-testid={`era-filter-${era.id}`}
              >
                {era.icon && <span>{era.icon}</span>}
                <span>{era.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedEra === era.id 
                    ? "bg-white/20 text-white" 
                    : "bg-slate-700 text-slate-400"
                }`}>
                  {eraCountsMap[era.id] || 0}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search players..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setSelectedLetter("all"); // Reset letter filter when searching
                  }}
                  className="pl-10"
                  data-testid="search-players"
                />
              </div>
            </div>
            
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="w-40" data-testid="filter-position">
                <SelectValue placeholder="All Positions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Positions</SelectItem>
                {positions.map(pos => (
                  <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="w-44" data-testid="filter-team">
                <SelectValue placeholder="All Teams" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Teams</SelectItem>
                {teams.map(team => (
                  <SelectItem key={team} value={team}>{team}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Alphabetical Navigation Slider */}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-slate-500 uppercase whitespace-nowrap">Jump to:</span>
              <button
                onClick={() => setSelectedLetter("all")}
                className={`px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  selectedLetter === "all"
                    ? "bg-[#7C2C3B] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
                data-testid="filter-letter-all"
              >
                ALL
              </button>
              <div className="flex-1 flex items-center gap-2">
                <span className="text-sm font-bold text-[#7C2C3B]">A</span>
                <div className="flex-1 relative">
                  <input
                    type="range"
                    min="0"
                    max="25"
                    value={selectedLetter === "all" ? 0 : selectedLetter.charCodeAt(0) - 65}
                    onChange={(e) => {
                      const letterIndex = parseInt(e.target.value);
                      const letter = String.fromCharCode(65 + letterIndex);
                      if (availableLetters.has(letter)) {
                        setSelectedLetter(letter);
                      } else {
                        // Find nearest available letter
                        for (let i = letterIndex; i <= 25; i++) {
                          const nextLetter = String.fromCharCode(65 + i);
                          if (availableLetters.has(nextLetter)) {
                            setSelectedLetter(nextLetter);
                            break;
                          }
                        }
                      }
                    }}
                    className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#7C2C3B] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#7C2C3B] [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#7C2C3B] [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
                    data-testid="alphabet-slider"
                  />
                </div>
                <span className="text-sm font-bold text-[#7C2C3B]">Z</span>
              </div>
              {selectedLetter !== "all" && (
                <div className="flex items-center gap-2">
                  <span className="w-8 h-8 rounded-lg bg-[#7C2C3B] text-white flex items-center justify-center font-bold text-lg" style={{ fontFamily: 'Barlow Condensed' }}>
                    {selectedLetter}
                  </span>
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    {filteredPlayers.length} players
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : filteredPlayers.length === 0 ? (
          <div className="empty-state">
            <Users className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-700 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>NO PLAYERS FOUND</h3>
            <p className="text-slate-500">
              {searchQuery ? "Try a different search term" : "Admin can add players via the admin panel"}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-4 text-sm text-slate-500">
              Showing {filteredPlayers.length} player{filteredPlayers.length !== 1 ? 's' : ''}
              {selectedEra !== "all" && ` from ${eras.find(e => e.id === selectedEra)?.label || selectedEra}`}
              {selectedLetter !== "all" && ` starting from "${selectedLetter}"`}
            </div>
            
            {/* Grouped by letter display */}
            {Object.keys(groupedPlayers).sort().map(letter => (
              <div key={letter} id={`letter-${letter}`} className="mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#7C2C3B] flex items-center justify-center">
                    <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed' }}>{letter}</span>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-[#7C2C3B] to-transparent"></div>
                  <span className="text-sm text-slate-400">{groupedPlayers[letter].length} player{groupedPlayers[letter].length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {groupedPlayers[letter].map(player => (
                    <PlayerCard 
                      key={player.id} 
                      player={player} 
                    />
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
